import React, { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { UserRole, Permission } from '../../schemas/auth';
import { Loader2 } from 'lucide-react';
import { AccessDenied } from './AccessDenied';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredPermissions?: Permission[] | Permission | null;
  requiredRoles?: UserRole[] | null;
}

// Protected Route Component
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermissions = null,
  requiredRoles = null,
}) => {
  const { isAuthenticated, isLoading, user, hasPermission, hasAllPermissions } = useAuth();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <Loader2 className='animate-spin text-blue-500' size={40} />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to='/login' replace />;
  }

  // Check role requirements
  if (requiredRoles && !requiredRoles.includes(user?.role as UserRole)) {
    return <AccessDenied />;
  }

  // Check permission requirements
  if (requiredPermissions) {
    const hasRequiredPermission = Array.isArray(requiredPermissions)
      ? hasAllPermissions(requiredPermissions)
      : hasPermission(requiredPermissions);

    if (!hasRequiredPermission) {
      return <AccessDenied />;
    }
  }

  // User is authenticated and has required permissions/roles
  return <>{children}</>;
};

interface PublicRouteProps {
  children: ReactNode;
}

// Public Route Component (redirects if already authenticated)
export const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <Loader2 className='animate-spin text-blue-500' size={40} />
      </div>
    );
  }

  // Redirect to dashboard if already authenticated
  if (isAuthenticated) {
    return <Navigate to='/' replace />;
  }

  return <>{children}</>;
};

interface RoleRouteProps {
  children: ReactNode;
  roles: UserRole[];
}

// Role-based route component
export const RoleRoute: React.FC<RoleRouteProps> = ({ children, roles }) => {
  return <ProtectedRoute requiredRoles={roles}>{children}</ProtectedRoute>;
};

interface PermissionRouteProps {
  children: ReactNode;
  permissions: Permission[] | Permission;
}

// Permission-based route component
export const PermissionRoute: React.FC<PermissionRouteProps> = ({ children, permissions }) => {
  return <ProtectedRoute requiredPermissions={permissions}>{children}</ProtectedRoute>;
};

export default ProtectedRoute;
